import { Clipboard } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { Subject, debounceTime, distinctUntilChanged, finalize, map } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

import {
  buildSpringCronPreview,
  composeScheduleEditor,
  CronPreviewResult,
  DEFAULT_SCHEDULE_EDITOR,
  parseScheduleEditor,
  ScheduleEditorType,
  ScheduleEditorValue
} from './spring-cron-preview';

export interface ScheduleTask {
  taskId: string;
  name: string;
  targetService: string;
  cronExpression: string;
  zoneId: string;
  status: 'Waiting' | 'Executing' | 'Disable' | 'Enable';
  version: number;
  latestExecutionStatus?: 'Dispatching' | 'Running' | 'StopRequested' | 'Stopped' | 'Succeeded' | 'Failed';
}

export interface ScheduleExecution {
  id?: string;
  createTime?: number;
  updateTime?: number;
  executionId: string;
  taskId: string;
  scheduleVersion?: number;
  scheduledAt?: number;
  status: 'Dispatching' | 'Running' | 'StopRequested' | 'Stopped' | 'Succeeded' | 'Failed';
  message?: string;
  hasOnceExecutionLog?: boolean;
}

export interface OnceTaskExecutionLog {
  id?: string;
  createTime?: number;
  updateTime?: number;
  onceTaskId: string;
  parentExecutionId: string;
  taskType?: string;
  entityId?: string;
  status: 'Scheduled' | 'Claimed' | 'Succeeded' | 'Retrying' | 'Failed' | 'Cancelled' | 'Reclaimed';
  attempt?: number;
  workerId?: string;
  message?: string;
}

interface CronPreset {
  expression: string;
  labelKey: string;
}

@Component({
  selector: 'app-scheduled-task-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    I18nPipe,
    NzButtonModule,
    NzIconModule,
    NzInputModule,
    NzInputNumberModule,
    NzModalModule,
    NzPaginationModule,
    NzSelectModule,
    NzSpinModule,
    NzTableModule,
    NzTagModule,
    NzToolTipModule,
    TestIdDirective
  ],
  templateUrl: './scheduled-task-management.component.html',
  styleUrls: ['./scheduled-task-management.component.less']
})
export class ScheduledTaskManagementComponent implements OnInit {
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly clipboard = inject(Clipboard);
  private readonly taskSearch$ = new Subject<string>();
  private readonly cronDisplayCache = new Map<string, { description: string; nextRuns: string[]; isValid: boolean }>();

  loading = false;
  tasks: ScheduleTask[] = [];
  taskTotal = 0;
  taskPageIndex = 0;
  taskPageSize = 15;
  searchKey = '';

  editModalVisible = false;
  saving = false;
  editingTask: ScheduleTask | null = null;
  editForm: Pick<ScheduleTask, 'taskId' | 'cronExpression'> = {
    taskId: '',
    cronExpression: ''
  };
  readonly cronPresets: CronPreset[] = [
    { expression: '0 */5 * * * ?', labelKey: 'scheduled-task.cron-preset.every-5-minutes' },
    { expression: '0 0 * * * ?', labelKey: 'scheduled-task.cron-preset.hourly' },
    { expression: '0 0 2 * * ?', labelKey: 'scheduled-task.cron-preset.daily-2am' },
    { expression: '0 0 9 ? * MON-FRI', labelKey: 'scheduled-task.cron-preset.weekdays-9am' }
  ];
  readonly scheduleTypes: Array<{ type: ScheduleEditorType; labelKey: string }> = [
    { type: 'minutes', labelKey: 'scheduled-task.cron-editor.minutes' },
    { type: 'hourly', labelKey: 'scheduled-task.cron-editor.hourly' },
    { type: 'daily', labelKey: 'scheduled-task.cron-editor.daily' },
    { type: 'weekdays', labelKey: 'scheduled-task.cron-editor.weekdays' },
    { type: 'weekly', labelKey: 'scheduled-task.cron-editor.weekly' },
    { type: 'monthly', labelKey: 'scheduled-task.cron-editor.monthly' },
    { type: 'custom', labelKey: 'scheduled-task.cron-editor.custom' }
  ];
  readonly weekDays = [
    { value: 'MON', labelKey: 'scheduled-task.weekday.mon' },
    { value: 'TUE', labelKey: 'scheduled-task.weekday.tue' },
    { value: 'WED', labelKey: 'scheduled-task.weekday.wed' },
    { value: 'THU', labelKey: 'scheduled-task.weekday.thu' },
    { value: 'FRI', labelKey: 'scheduled-task.weekday.fri' },
    { value: 'SAT', labelKey: 'scheduled-task.weekday.sat' },
    { value: 'SUN', labelKey: 'scheduled-task.weekday.sun' }
  ];
  scheduleEditor: ScheduleEditorValue = { ...DEFAULT_SCHEDULE_EDITOR };
  cronPreview: CronPreviewResult = { status: 'empty', nextRuns: [] };
  statusUpdatingTaskId: string | null = null;

  executionModalVisible = false;
  executionLoading = false;
  selectedTask: ScheduleTask | null = null;
  executions: ScheduleExecution[] = [];
  executionPageIndex = 0;
  executionPageSize = 20;
  executionTotal = 0;

  onceLogModalVisible = false;
  onceLogLoading = false;
  selectedExecution: ScheduleExecution | null = null;
  onceLogs: OnceTaskExecutionLog[] = [];

  get executionModalTitle(): string {
    const name = this.selectedTask?.name || this.selectedTask?.taskId || '';
    return name ? `${this.i18n.fanyi('scheduled-task.execution-records')} · ${name}` : this.i18n.fanyi('scheduled-task.execution-records');
  }

  get onceLogModalTitle(): string {
    const id = this.selectedExecution?.executionId || '';
    return id ? `${this.i18n.fanyi('scheduled-task.once-logs')} · ${id}` : this.i18n.fanyi('scheduled-task.once-logs');
  }

  get canSaveCron(): boolean {
    return Boolean(this.editForm.taskId && this.editForm.cronExpression.trim()) && this.cronPreview.status !== 'invalid';
  }

  constructor(
    private readonly http: HttpService,
    private readonly message: NzMessageService,
    private readonly cdr: ChangeDetectorRef
  ) {
    if (this.i18n.change) {
      this.i18n.change.pipe(takeUntilDestroyed()).subscribe(() => {
        this.cronDisplayCache.clear();
        this.cdr.markForCheck();
      });
    }

    this.taskSearch$
      .pipe(
        map(value => value.trim()),
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed()
      )
      .subscribe(name => {
        this.searchKey = name;
        this.taskPageIndex = 0;
        this.loadTasks();
      });
  }

  ngOnInit(): void {
    this.loadTasks();
  }

  loadTasks(): void {
    this.loading = true;
    const name = this.searchKey.trim();
    this.http
      .post(ConstantUtil.ADMIN_SCHEDULE_V1_LIST, {
        pageIndex: this.taskPageIndex,
        pageSize: this.taskPageSize,
        orderBy: 'createTime',
        direction: 'desc',
        ...(name ? { name } : {})
      })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          if (!res?.success) {
            this.message.error(res?.msg ?? this.i18n.fanyi('scheduled-task.message.load-failed'));
            return;
          }
          const data = res.data;
          this.cronDisplayCache.clear();
          this.tasks = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
          this.taskTotal = Array.isArray(data) ? this.tasks.length : Number(data?.totalCount ?? data?.count ?? this.tasks.length);
        },
        error: () => this.message.error(this.i18n.fanyi('scheduled-task.message.load-failed'))
      });
  }

  refresh(): void {
    this.loadTasks();
  }

  onTaskSearchChange(value: string): void {
    this.searchKey = value;
    this.taskSearch$.next(value);
  }

  resetSearch(): void {
    this.onTaskSearchChange('');
  }

  changeTaskPage(page: number): void {
    this.taskPageIndex = page - 1;
    this.loadTasks();
  }

  changeTaskPageSize(pageSize: number): void {
    this.taskPageSize = pageSize;
    this.taskPageIndex = 0;
    this.loadTasks();
  }

  openEdit(task: ScheduleTask): void {
    this.editingTask = task;
    this.editForm = {
      taskId: task.taskId,
      cronExpression: task.cronExpression
    };
    this.scheduleEditor = parseScheduleEditor(task.cronExpression);
    this.refreshCronPreview();
    this.editModalVisible = true;
  }

  closeEdit(): void {
    if (this.saving) return;
    this.editModalVisible = false;
    this.editingTask = null;
  }

  onCronExpressionChange(value: string): void {
    this.editForm.cronExpression = value;
    this.scheduleEditor = parseScheduleEditor(value);
    this.refreshCronPreview();
  }

  applyCronPreset(preset: CronPreset): void {
    this.onCronExpressionChange(preset.expression);
  }

  setScheduleType(type: ScheduleEditorType): void {
    if (type === 'custom') {
      this.scheduleEditor = { ...this.scheduleEditor, type };
      return;
    }
    this.updateScheduleEditor({ type });
  }

  updateScheduleEditor(patch: Partial<ScheduleEditorValue>): void {
    this.scheduleEditor = { ...this.scheduleEditor, ...patch };
    const expression = composeScheduleEditor(this.scheduleEditor);
    if (!expression) return;
    this.editForm.cronExpression = expression;
    this.refreshCronPreview();
  }

  saveTask(): void {
    const cronExpression = this.editForm.cronExpression.trim();
    if (!this.editForm.taskId || !cronExpression || this.cronPreview.status === 'invalid') {
      this.message.error(this.i18n.fanyi('scheduled-task.message.cron-required'));
      return;
    }

    const payload = {
      taskId: this.editForm.taskId,
      cronExpression,
      status: this.editingTask?.status === 'Disable' ? 'Disable' : 'Enable'
    };
    this.saving = true;
    this.http
      .post(ConstantUtil.ADMIN_SCHEDULE_V1_UPDATE, payload)
      .pipe(
        finalize(() => {
          this.saving = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          if (!res?.success) {
            this.message.error(res?.msg ?? this.i18n.fanyi('scheduled-task.message.update-failed'));
            return;
          }
          const updated = res.data ?? { ...this.editingTask, ...payload };
          this.cronDisplayCache.clear();
          this.tasks = this.tasks.map(task => (task.taskId === payload.taskId ? { ...task, ...updated } : task));
          this.message.success(this.i18n.fanyi('scheduled-task.message.update-success'));
          this.editModalVisible = false;
          this.editingTask = null;
        },
        error: () => this.message.error(this.i18n.fanyi('scheduled-task.message.update-failed'))
      });
  }

  updateTaskStatus(task: ScheduleTask, status: 'Enable' | 'Disable'): void {
    if (task.status === status || this.statusUpdatingTaskId) return;

    const payload = {
      taskId: task.taskId,
      cronExpression: task.cronExpression,
      status
    };
    this.statusUpdatingTaskId = task.taskId;
    this.http
      .post(ConstantUtil.ADMIN_SCHEDULE_V1_UPDATE, payload)
      .pipe(
        finalize(() => {
          this.statusUpdatingTaskId = null;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          if (!res?.success) {
            this.message.error(res?.msg ?? this.i18n.fanyi('scheduled-task.message.update-failed'));
            return;
          }
          const updated = res.data ?? { ...task, status };
          this.tasks = this.tasks.map(item => (item.taskId === task.taskId ? { ...item, ...updated } : item));
          this.message.success(this.i18n.fanyi('scheduled-task.message.update-success'));
        },
        error: () => this.message.error(this.i18n.fanyi('scheduled-task.message.update-failed'))
      });
  }

  nextTaskStatus(task: ScheduleTask): 'Enable' | 'Disable' {
    return task.status === 'Enable' ? 'Disable' : 'Enable';
  }

  viewExecutions(task: ScheduleTask): void {
    this.selectedTask = task;
    this.executionModalVisible = true;
    this.executionPageIndex = 0;
    this.loadExecutions();
  }

  changeExecutionPage(page: number): void {
    this.executionPageIndex = page - 1;
    this.loadExecutions();
  }

  changeExecutionPageSize(pageSize: number): void {
    this.executionPageSize = pageSize;
    this.executionPageIndex = 0;
    this.loadExecutions();
  }

  private loadExecutions(): void {
    const task = this.selectedTask;
    if (!task) return;
    this.executionLoading = true;
    const url = ConstantUtil.ADMIN_SCHEDULE_V1_EXECUTION_LIST.replace('{taskId}', encodeURIComponent(task.taskId));
    this.http
      .post(url, {
        pageIndex: this.executionPageIndex,
        pageSize: this.executionPageSize,
        orderBy: 'scheduledAt',
        direction: 'desc'
      })
      .pipe(
        finalize(() => {
          this.executionLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          if (!res?.success) {
            this.message.error(res?.msg ?? this.i18n.fanyi('scheduled-task.message.execution-load-failed'));
            return;
          }
          this.executions = Array.isArray(res.data?.items) ? res.data.items : [];
          this.executionTotal = Number(res.data?.totalCount ?? 0);
        },
        error: () => this.message.error(this.i18n.fanyi('scheduled-task.message.execution-load-failed'))
      });
  }

  private refreshCronPreview(): void {
    this.cronPreview = buildSpringCronPreview(this.editForm.cronExpression, this.editingTask?.zoneId || 'UTC');
  }

  formatCronDescription(preview: CronPreviewResult): string {
    if (preview.status === 'empty') return '--';
    if (preview.status === 'invalid') return this.i18n.fanyi(preview.errorKey || 'scheduled-task.cron-error.invalid');
    if (preview.status === 'unsupported') return this.i18n.fanyi('scheduled-task.cron-preview.unsupported-title');
    if (!preview.descriptionKey) return this.i18n.fanyi('scheduled-task.cron-preview.advanced');

    const params: Record<string, string | number> = { ...preview.descriptionParams };
    if (params['weekdayKey']) {
      params['weekday'] = this.i18n.fanyi(String(params['weekdayKey']));
    }
    return this.i18n.fanyi(preview.descriptionKey, params);
  }

  getCronDisplayInfo(task?: ScheduleTask | null): { description: string; nextRuns: string[]; isValid: boolean } {
    const expr = (task?.cronExpression || '').trim();
    if (!expr) {
      return { description: '--', nextRuns: [], isValid: false };
    }
    const zoneId = task?.zoneId || 'UTC';
    const lang = (this.i18n as any)?.currentLang || '';
    const cacheKey = `${expr}__${zoneId}__${lang}`;
    const cached = this.cronDisplayCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const preview = buildSpringCronPreview(expr, zoneId);
    const description = this.formatCronDescription(preview);
    const result = {
      description,
      nextRuns: preview.nextRuns || [],
      isValid: preview.status === 'valid'
    };
    this.cronDisplayCache.set(cacheKey, result);
    return result;
  }

  copyCron(event: MouseEvent, cron?: string): void {
    event.stopPropagation();
    const value = (cron || '').trim();
    if (!value) return;
    this.clipboard.copy(value);
    this.message.success(this.i18n.fanyi('scheduled-task.cron-copied'));
  }

  closeExecutions(): void {
    this.executionModalVisible = false;
    this.selectedTask = null;
    this.executions = [];
    this.executionTotal = 0;
  }

  viewOnceLogs(execution: ScheduleExecution): void {
    this.selectedExecution = execution;
    this.onceLogModalVisible = true;
    this.onceLogLoading = true;
    const url = ConstantUtil.ADMIN_SCHEDULE_V1_EXECUTION_ONCE_LIST.replace('{executionId}', encodeURIComponent(execution.executionId));
    this.http
      .post(url, {})
      .pipe(
        finalize(() => {
          this.onceLogLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          if (!res?.success) {
            this.message.error(res?.msg ?? this.i18n.fanyi('scheduled-task.message.once-log-load-failed'));
            return;
          }
          const logs: OnceTaskExecutionLog[] = Array.isArray(res.data) ? res.data : [];
          this.onceLogs = [...logs].sort(
            (left, right) => Number(right.createTime ?? right.updateTime ?? 0) - Number(left.createTime ?? left.updateTime ?? 0)
          );
        },
        error: () => this.message.error(this.i18n.fanyi('scheduled-task.message.once-log-load-failed'))
      });
  }

  closeOnceLogs(): void {
    this.onceLogModalVisible = false;
    this.selectedExecution = null;
    this.onceLogs = [];
  }

  statusLabel(status?: string): string {
    const value = String(status ?? '').trim();
    if (!value) return '--';

    const keyByStatus: Record<string, string> = {
      waiting: 'scheduled-task.status.waiting',
      executing: 'scheduled-task.status.executing',
      enable: 'scheduled-task.status.enable',
      disable: 'scheduled-task.status.disable',
      dispatching: 'scheduled-task.status.dispatching',
      running: 'scheduled-task.status.running',
      stoprequested: 'scheduled-task.status.stop-requested',
      stopped: 'scheduled-task.status.stopped',
      succeeded: 'scheduled-task.status.succeeded',
      failed: 'scheduled-task.status.failed',
      scheduled: 'scheduled-task.status.scheduled',
      claimed: 'scheduled-task.status.claimed',
      retrying: 'scheduled-task.status.retrying',
      cancelled: 'scheduled-task.status.cancelled',
      reclaimed: 'scheduled-task.status.reclaimed'
    };
    const key = keyByStatus[value.toLowerCase().replace(/[-_\s]/g, '')];
    return key ? this.i18n.fanyi(key) : value;
  }

  taskStatusColor(status?: string): string {
    const value = String(status ?? '')
      .trim()
      .toLowerCase();
    if (value === 'enable') return 'green';
    if (value === 'disable') return 'default';
    if (value === 'executing') return 'blue';
    return 'gold';
  }

  executionStatusColor(status?: string): string {
    const value = String(status ?? '')
      .trim()
      .toLowerCase();
    if (value === 'succeeded') return 'green';
    if (value === 'failed') return 'red';
    if (value === 'running') return 'blue';
    if (value === 'stoprequested' || value === 'stop-requested') return 'orange';
    if (value === 'stopped' || value === 'cancelled') return 'default';
    return 'gold';
  }
}
